import { Maybe, StateAssert } from '@csp/csp-common-model';
import { BinaryFileData } from '@csp/dmdp-api-client';
import { DownloadFileService } from '../service/DownloadFileService';

const getFormatFromContentType = (contentType: string): Maybe<string> => {
  switch (contentType) {
    case 'image/png':
      return 'png';
    case 'image/jpeg':
      return 'jpeg';
    case 'image/gif':
      return 'gif';
    case 'application/zip':
      return 'zip';
  }
  return undefined;
};

const splitFileNameAndExt = (filename: string): { filename: string; extension?: string } => {
  const dotIndex = filename.lastIndexOf('.');
  if (dotIndex === -1) {
    return { filename };
  }
  return { filename: filename.substring(0, dotIndex), extension: filename.substring(dotIndex + 1, filename.length) };
};

const initiateDownload = (fileNameWithExt: string, getBinaryFileData: BinaryFileData): void => {
  const { blob, contentType } = getBinaryFileData;
  const { filename, extension } = splitFileNameAndExt(fileNameWithExt);

  DownloadFileService.download({
    data: blob,
    filename,
    // Prefer deriving extension from content type, fallback to the extension from the filename of the current file
    format: getFormatFromContentType(contentType) ?? extension,
    mime: contentType,
  });
};

const pollForBinaryData = async (
  getBinaryFileData: () => Promise<Maybe<BinaryFileData>>,
  fileNameWithExt: string,
  signal: Maybe<AbortSignal>,
  delay: number = 1000,
): Promise<void> => {
  signal?.throwIfAborted();

  const binaryData = await getBinaryFileData();

  if (binaryData?.blob) {
    return initiateDownload(fileNameWithExt, binaryData);
  }

  await new Promise(resolve => setTimeout(resolve, delay));
  return await pollForBinaryData(getBinaryFileData, fileNameWithExt, signal, delay);
};

const createDownloadFileHandler =
  <Args extends unknown[]>(
    getBinaryFileData: (...args: Args) => Promise<Maybe<BinaryFileData>>,
    options?: { poll: boolean; signal?: AbortSignal; delay?: number },
  ) =>
  async (fileNameWithExt: string, ...args: Args): Promise<void> => {
    if (options?.poll) {
      return await pollForBinaryData(() => getBinaryFileData(...args), fileNameWithExt, options.signal, options.delay);
    }
    const binaryData = await getBinaryFileData(...args);
    StateAssert.isDefined(binaryData, 'No binary data returned');
    initiateDownload(fileNameWithExt, binaryData);
  };

export const FileUtil = {
  createDownloadFileHandler,

  /** Exported for test purposes */
  pollForBinaryData,
};
